import React, { useState, useEffect } from 'react';
import { 
  WifiIcon, 
  ExclamationCircleIcon,
  ArrowPathIcon,
  CheckCircleIcon 
} from '@heroicons/react/24/outline';
import { useWebSocket } from '../contexts/WebSocketContext';

const NetworkErrorBoundary = ({ children }) => {
  const { isConnected, error: wsError } = useWebSocket();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showBanner, setShowBanner] = useState(false);
  const [autoRetryCount, setAutoRetryCount] = useState(0);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Show banner when disconnected
  useEffect(() => {
    const shouldShowBanner = !isOnline || (!isConnected && wsError);
    setShowBanner(shouldShowBanner);

    // Auto-retry logic
    if (shouldShowBanner && autoRetryCount < 3) {
      const timer = setTimeout(() => {
        if (!isOnline) {
          setAutoRetryCount(prev => prev + 1);
        }
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, isConnected, wsError, autoRetryCount]);

  const handleRetry = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <>
      {children}
      
      {/* Network Error Banner */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
          <div className="max-w-4xl mx-auto">
            <div className={`
              rounded-lg shadow-lg p-4 
              ${!isOnline ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}
            `}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {!isOnline ? (
                    <WifiIcon className="w-6 h-6 text-red-600" />
                  ) : (
                    <ExclamationCircleIcon className="w-6 h-6 text-yellow-600" />
                  )}
                  
                  <div>
                    <h3 className={`font-semibold ${
                      !isOnline ? 'text-red-800' : 'text-yellow-800'
                    }`}>
                      {!isOnline ? 'No Internet Connection' : 'WebSocket Disconnected'}
                    </h3>
                    <p className={`text-sm ${
                      !isOnline ? 'text-red-600' : 'text-yellow-600'
                    }`}>
                      {!isOnline 
                        ? 'Please check your internet connection.'
                        : 'Connection to the server has been lost. Attempting to reconnect...'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handleRetry}
                    className={`
                      px-3 py-1.5 rounded-md text-sm font-medium transition-colors
                      ${!isOnline 
                        ? 'bg-red-600 text-white hover:bg-red-700' 
                        : 'bg-yellow-600 text-white hover:bg-yellow-700'}
                    `}
                  >
                    <ArrowPathIcon className="w-4 h-4 inline mr-1" />
                    Retry
                  </button>
                  
                  <button
                    onClick={() => setShowBanner(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {autoRetryCount > 0 && (
                <div className="mt-2 text-xs text-gray-600">
                  Auto-retry attempt {autoRetryCount} of 3...
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full Screen Network Error (for critical disconnections) */}
      {!isOnline && autoRetryCount >= 3 && (
        <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
          <div className="text-center p-8 max-w-md">
            <WifiIcon className="w-24 h-24 text-gray-300 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              You're Offline
            </h2>
            <p className="text-gray-600 mb-6">
              It seems you've lost your internet connection. Please check your network settings and try again.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={handleRetry}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ArrowPathIcon className="w-5 h-5 inline mr-2" />
                Retry Connection
              </button>
              
              <button
                onClick={handleGoHome}
                className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connection Restored Notification */}
      {isOnline && isConnected && autoRetryCount > 0 && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in">
          <div className="bg-green-50 border border-green-200 rounded-lg shadow-lg p-4 flex items-center gap-3">
            <CheckCircleIcon className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-sm font-medium text-green-800">
                Connection Restored
              </p>
              <p className="text-xs text-green-600">
                You're back online!
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NetworkErrorBoundary;